// src/pages/pessoas/FornecedorCad.js
import React, { useState, useEffect } from 'react';

import Input from '../../components/input/Input';
import { Fornecedor } from '../../models/pessoas/Fornecedor.js';
import { useFornecedor } from '../../contexts/pessoas/FornecedorContext';
import InputCheck from '../../components/input/InputCheck.js';
import { formatDate } from '../../utils/dateTimeUtils.js';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import EnderecoForm from '../../components/form/pessoa/EnderecoForm.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import ModalContainer from '../../components/modalForm/ModalContainer.js';
import ModalBody from '../../components/modalForm/ModalBody.js';
import ModalFooter from '../../components/modalForm/ModalFooter.js';
import ModalHeader from '../../components/modalForm/ModalHeader.js';
import ModalTabs from '../../components/modalForm/ModalTabs';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const FornecedorCad = ({ show, onHide, initialFornecedor, onSaveFornecedor }) => {
    const { fornecedores, addFornecedor, updateFornecedor, deleteFornecedor, showFornecedorSearch } = useFornecedor();
    const [fornecedorAux, setFornecedorAux] = useState();

    const [novoReg, setNovoReg] = useState(false);
    const [activeTab, setActiveTab] = useState('geral');
    const { showMessage } = useMessage();
    const { showYesNo } = useYesNo();
    const [tipoPessoa, setTipoPessoa] = useState('J'); // padrão para fornecedores
    const [isSaving, setIsSaving] = useState(false);

    const handleBuscarFornecedor = async () => {
        const fornecedorSel = await showFornecedorSearch();
        if (fornecedorSel) {
            setFornecedorAux(fornecedorSel);
            setTipoPessoa(fornecedorSel.pessoa?.fisicaId ? 'F' : 'J');
            handleEditar(fornecedorSel.id);
        } else {
            setFornecedorAux(new Fornecedor());
            setTipoPessoa('J');
        }
    };

    const handleEditar = (id) => {
        if (!id) {
            showMessage("Selecione um fornecedor para editar!");
            return;
        }
        setNovoReg(false);
    };

    const handleExit = () => {
        const fornecedorSelecionado = fornecedores.find(f => f.id.toString() === fornecedorAux?.id?.toString());
        if (fornecedorSelecionado) {
            setFornecedorAux(fornecedorSelecionado);
            setTipoPessoa(fornecedorSelecionado.pessoa?.fisicaId ? 'F' : 'J');
            handleEditar(fornecedorSelecionado.id);
        } else {
            setFornecedorAux(new Fornecedor());
            setTipoPessoa('J');
        }
    };

    useEffect(() => {
        if (!initialFornecedor) {
            setFornecedorAux(new Fornecedor());
            setNovoReg(true);
        } else {
            setFornecedorAux(initialFornecedor);
            setNovoReg(true);
        }
    }, [initialFornecedor]);

    const handleNovo = () => {
        setFornecedorAux(new Fornecedor());
        setNovoReg(true);
        setTipoPessoa('J');
        setIsSaving(false);
    };

    const handleDelete = async () => {
        if (!fornecedorAux.id) {
            showMessage("Selecione um fornecedor para excluir!");
        } else {
            const resposta = await showYesNo("Deseja excluir o fornecedor selecionado?");
            if (resposta) {
                await deleteFornecedor(fornecedorAux.id);
                handleNovo();
            }
        }
    };

    const handleSave = async () => {
        if (isSaving) return; // Previne cliques múltiplos

        setIsSaving(true);
        try {
            if (!fornecedorAux.pessoa?.nome) {
                showMessage("Nome não preenchido!");
                return;
            }

            const fornecedorPayload = new Fornecedor(fornecedorAux);

            if (tipoPessoa === 'F') {
                fornecedorPayload.pessoa.juridica = null;
                fornecedorPayload.pessoa.juridicaId = null;
            } else {
                fornecedorPayload.pessoa.fisica = null;
                fornecedorPayload.pessoa.fisicaId = null;
            }

            const fornecedorSalvo = !fornecedorPayload.id
                ? await addFornecedor(fornecedorPayload)
                : await updateFornecedor(fornecedorPayload);

            if (!fornecedorSalvo) return;

            onSaveFornecedor?.(fornecedorSalvo);
            if (initialFornecedor && !onSaveFornecedor) onHide();
            handleNovo();
        } catch (error) {
            showMessage(error?.message || error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleTabChange = (tab) => (e) => {
        e.preventDefault();
        setActiveTab(tab);
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura='650px' zIndex={1060} variant="cadastro" onHide={onHide}>
            <ModalHeader title={novoReg ? 'Novo Fornecedor' : 'Alterar Fornecedor'} onHide={onHide} />
            <ModalBody>
                <form>
                    <div className="input-group">
                        <Input
                            label="Código"
                            value={fornecedorAux.id || ''}
                            onChange={(e) => setFornecedorAux({ ...fornecedorAux, id: e.target.value })}
                            disabled={!novoReg}
                            btnBuscar={handleBuscarFornecedor}
                            onBlur={handleExit}
                        />
                        <Input
                            label="Nome"
                            value={fornecedorAux.pessoa?.nome || ''}
                            onChange={(e) =>
                                setFornecedorAux({
                                    ...fornecedorAux,
                                    pessoa: { ...fornecedorAux?.pessoa, nome: e.target.value },
                                })
                            }
                            size={300}
                        />
                        <Input
                            label="Data Cad."
                            value={fornecedorAux.dataCad ? formatDate(fornecedorAux.dataCad) : ''}
                            disabled
                            size={100}
                        />
                    </div>

                    <ModalTabs  style={{ marginTop: '10px' }}>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'geral' ? 'active' : ''}`}
                                onClick={handleTabChange('geral')}
                            >
                                Geral
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'contato' ? 'active' : ''}`}
                                onClick={handleTabChange('contato')}
                            >
                                Contatos
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'endereco' ? 'active' : ''}`}
                                onClick={handleTabChange('endereco')}
                            >
                                Endereço
                            </button>
                        </li>
                    </ModalTabs>

                    <ModalTabs.Panel >
                        {activeTab === 'geral' && (
                            <div className="section">
                                <div className="input-group">
                                    <InputCheck
                                        label="Pessoa Física"
                                        checked={tipoPessoa === 'F'}
                                        onChange={() => setTipoPessoa('F')}
                                    />
                                    <InputCheck
                                        label="Pessoa Jurídica"
                                        checked={tipoPessoa === 'J'}
                                        onChange={() => setTipoPessoa('J')}
                                    />
                                </div>
                                {tipoPessoa === 'F' && (
                                    <>
                                        <div className="input-group">
                                            <Input
                                                label="CPF"
                                                value={fornecedorAux.pessoa?.fisica?.cpf || ''}
                                                onChange={(e) =>
                                                    setFornecedorAux({
                                                        ...fornecedorAux,
                                                        pessoa: {
                                                            ...fornecedorAux.pessoa,
                                                            fisica: {
                                                                ...fornecedorAux.pessoa?.fisica,
                                                                cpf: e.target.value,
                                                            },
                                                        },
                                                    })
                                                }
                                                mask="999.999.999-99"
                                                size={150}
                                            />
                                            <Input
                                                label="RG"
                                                value={fornecedorAux.pessoa?.fisica?.rg || ''}
                                                onChange={(e) =>
                                                    setFornecedorAux({
                                                        ...fornecedorAux,
                                                        pessoa: {
                                                            ...fornecedorAux.pessoa,
                                                            fisica: {
                                                                ...fornecedorAux.pessoa?.fisica,
                                                                rg: e.target.value,
                                                            },
                                                        },
                                                    })
                                                }
                                                size={150}
                                            />
                                        </div>
                                    </>
                                )}
                                {tipoPessoa === 'J' && (
                                    <>
                                        <div className="input-group">
                                            <Input
                                                label="CNPJ"
                                                value={fornecedorAux.pessoa?.juridica?.cnpj || ''}
                                                onChange={(e) =>
                                                    setFornecedorAux({
                                                        ...fornecedorAux,
                                                        pessoa: {
                                                            ...fornecedorAux.pessoa,
                                                            juridica: {
                                                                ...fornecedorAux.pessoa?.juridica,
                                                                cnpj: e.target.value,
                                                            },
                                                        },
                                                    })
                                                }
                                                mask="99.999.999/9999-99"
                                                size={170}
                                            />
                                            <Input
                                                label="Razão Social"
                                                value={fornecedorAux.pessoa?.juridica?.razaoSocial || ''}
                                                onChange={(e) =>
                                                    setFornecedorAux({
                                                        ...fornecedorAux,
                                                        pessoa: {
                                                            ...fornecedorAux.pessoa,
                                                            juridica: {
                                                                ...fornecedorAux.pessoa?.juridica,
                                                                razaoSocial: e.target.value,
                                                            },
                                                        },
                                                    })
                                                }
                                                size={300}
                                            />
                                        </div>
                                        <div className="input-group">
                                            <Input
                                                label="Inscrição Estadual"
                                                value={fornecedorAux.pessoa?.juridica?.ie || ''}
                                                onChange={(e) =>
                                                    setFornecedorAux({
                                                        ...fornecedorAux,
                                                        pessoa: {
                                                            ...fornecedorAux.pessoa,
                                                            juridica: {
                                                                ...fornecedorAux.pessoa?.juridica,
                                                                ie: e.target.value,
                                                            },
                                                        },
                                                    })
                                                }
                                                size={150}
                                            />
                                            <Input
                                                label="Inscrição Municipal"
                                                value={fornecedorAux.pessoa?.juridica?.im || ''}
                                                onChange={(e) =>
                                                    setFornecedorAux({
                                                        ...fornecedorAux,
                                                        pessoa: {
                                                            ...fornecedorAux.pessoa,
                                                            juridica: {
                                                                ...fornecedorAux.pessoa?.juridica,
                                                                im: e.target.value,
                                                            },
                                                        },
                                                    })
                                                }
                                                size={150}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {activeTab === 'contato' && (
                            <div className="section">
                                <div className="input-group">
                                    <Input
                                        label="Telefone"
                                        value={fornecedorAux.pessoa?.telefone?.numero || ''}
                                        onChange={(e) =>
                                            setFornecedorAux({
                                                ...fornecedorAux,
                                                pessoa: {
                                                    ...fornecedorAux.pessoa,
                                                    telefone: {
                                                        ...fornecedorAux.pessoa?.telefone,
                                                        numero: e.target.value,
                                                    },
                                                },
                                            })
                                        }
                                        size={150}
                                        mask="(99) 9 9999-9999"
                                    />
                                    <Input
                                        label="E-mail"
                                        value={fornecedorAux.pessoa?.email?.enderecoEmail || ''}
                                        onChange={(e) =>
                                            setFornecedorAux({
                                                ...fornecedorAux,
                                                pessoa: {
                                                    ...fornecedorAux.pessoa,
                                                    email: {
                                                        ...fornecedorAux.pessoa?.email,
                                                        enderecoEmail: e.target.value,
                                                    },
                                                },
                                            })
                                        }
                                        size={300}
                                    />
                                </div>
                            </div>
                        )}
                        {activeTab === 'endereco' && (
                            <EnderecoForm
                                endereco={fornecedorAux.pessoa?.endereco}
                                setEndereco={(newEndereco) =>
                                    setFornecedorAux({
                                        ...fornecedorAux,
                                        pessoa: {
                                            ...fornecedorAux.pessoa,
                                            endereco: newEndereco,
                                        },
                                    })
                                }
                            />
                        )}
                    </ModalTabs.Panel>
                </form>
            </ModalBody>
            <ModalFooter>
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" onClick={handleSave} disabled={isSaving} action="save">
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" onClick={handleDelete} disabled={novoReg || isSaving} action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" onClick={handleNovo} disabled={novoReg || isSaving} action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>
        </ModalContainer>
    );
};

export default FornecedorCad;

